// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { loadRecentDocuments } from "@conklin/App/Handlers/XNG/LoadRecentDocuments";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import DataTable, { DataTableProps } from "@conklin/MobiusOverrides/DataTable/DataTable";
import DataTableBody, { DataTableBodyProps } from "@conklin/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@conklin/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead, { DataTableHeadProps } from "@conklin/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@conklin/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@conklin/MobiusOverrides/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@conklin/MobiusOverrides/Link/Link";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

interface RecentDocumentsStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    emptyTableMessage?: TypographyPresentationProps;

    dataTable?: DataTableProps;
    tableHead?: DataTableHeadProps;
    headerCell?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    cell?: DataTableCellBaseProps;
    cellText?: LinkPresentationProps;
    text?: TypographyPresentationProps;
}

export const RecentDocumentsStyles: RecentDocumentsStyles = {};

const styles = RecentDocumentsStyles;

const mapStateToProps = (state: CustomAppState) => {
    return {
        isAuthenticated: xIsAuthenticated(state),
        recentDocuments: state.xRecentDocuments.documents,
        rememberMe: getSession(state).rememberMe,
    };
};

const mapDispatchToProps = {
    loadRecentDocuments,
};

interface OwnProps extends WidgetProps {
    fields: {
        maxNumberOfDocuments: number;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RecentDocuments: FC<Props> = ({ fields, isAuthenticated, recentDocuments, loadRecentDocuments, rememberMe }) => {
    useEffect(() => {
        if (!rememberMe) {
            loadRecentDocuments({ LookBackDays: 365, DocumentsToReturn: fields.maxNumberOfDocuments });
        }
    }, []);

    if (!isAuthenticated) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title} as="h3">
                    {siteMessage("Custom_Purchased_Documents_Title")}
                </Typography>
            </StyledWrapper>
            {recentDocuments?.length ? (
                <DataTable {...styles.dataTable} useDesktopViewForMobile={true}>
                    <DataTableHead {...styles.tableHead}>
                        <DataTableHeader {...styles.headerCell}>{translate("File Name")}</DataTableHeader>
                        <DataTableHeader {...styles.headerCell}>{translate("Item Code")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.tableBody}>
                        {recentDocuments?.map(document => {
                            return (
                                <DataTableRow key={document.name + document.erpNumber} {...styles.tableRow}>
                                    <DataTableCell {...styles.cell}>
                                        <Link target="_blank" href={document.filePath} {...styles.cellText}>
                                            {document.name}
                                        </Link>
                                    </DataTableCell>
                                    <DataTableCell {...styles.cell}>
                                        {document.productPathUrl ? (
                                            <Link href={document.productPathUrl} {...styles.cellText}>
                                                {document.erpNumber}
                                            </Link>
                                        ) : (
                                            <Typography {...styles.text}>{document.erpNumber}</Typography>
                                        )}
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                    </DataTableBody>
                </DataTable>
            ) : (
                <Typography {...styles.emptyTableMessage}>
                    {siteMessage("Custom_No_Recently_Purchased_Documents")}
                </Typography>
            )}
        </StyledWrapper>
    );
};

const RecentDocumentsWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentDocuments),
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        fieldDefinitions: [
            {
                name: "maxNumberOfDocuments",
                fieldType: "General",
                editorTemplate: "IntegerField",
                defaultValue: 6,
            },
        ],
    },
};

export default RecentDocumentsWidget;
